import wx from 'weixin-js-sdk';
import api from '../../http/index.js';

// 微信JSSDK配置对象
const wechatConfig = {
  // 调试日志回调函数
  _debugCallback: null,
  
  // 设置调试回调
  setDebugCallback: function(callback) {
    this._debugCallback = callback;
  },
  
  // 记录调试日志
  _log: function(message, data = null) {
    console.log(`[JSSDK] ${message}`, data);
    if (typeof this._debugCallback === 'function') {
      this._debugCallback(message, data);
    }
  },
  // 检测是否为微信环境
  isWechatEnvironment: () => {
    const ua = window.navigator.userAgent.toLowerCase();
    return ua.indexOf('micromessenger') > -1;
  },
  
  // 防抖定时器
  _initTimer: null,
  
  // 是否已初始化
  _isInitialized: false,
  
  // 微信配置缓存，按URL缓存
  _configCache: {},
  
  // 缓存过期时间（毫秒，默认1小时）
  _cacheExpireTime: 60 * 60 * 1000,
  
  // 初始化JSSDK
  init: async (shareConfig = {}) => {
    // 只有在微信环境中才进行JSSDK初始化
    if (!wechatConfig.isWechatEnvironment()) {
      wechatConfig._log('当前非微信环境，跳过JSSDK初始化');
      return;
    }
    
    // 优化：如果已经初始化过，并且分享配置没有变化，跳过重复初始化
    if (wechatConfig._isInitialized && wechatConfig._lastShareConfig && 
        JSON.stringify(wechatConfig._lastShareConfig) === JSON.stringify(shareConfig)) {
      wechatConfig._log('JSSDK已初始化且分享配置未变化，跳过重复初始化');
      return;
    }
    
    wechatConfig._log('开始初始化JSSDK', shareConfig);
    
    // 保存当前分享配置
    wechatConfig._lastShareConfig = shareConfig;
    
    // 防抖处理，避免短时间内多次调用
    if (wechatConfig._initTimer) {
      clearTimeout(wechatConfig._initTimer);
    }
    
    wechatConfig._initTimer = setTimeout(async () => {
      try {
        // 获取当前页面URL（不包含#及其后面内容）
        const url = window.location.href.split('#')[0];
        
        // 优化：所有页面共享同一个缓存，使用固定缓存键
        // 因为微信JSSDK配置通常是基于域名的，所有页面可以共享同一个配置
        const cacheKey = 'wechat_jssdk_config'; // 固定缓存键
        const now = Date.now();
        let jssdkConfig = null;
        let response = null;
        
        // 检查缓存是否存在且未过期
        if (wechatConfig._configCache[cacheKey] && 
            (now - wechatConfig._configCache[cacheKey].timestamp) < wechatConfig._cacheExpireTime) {
          // 从缓存获取配置
          jssdkConfig = wechatConfig._configCache[cacheKey].config;
          wechatConfig._log('从缓存获取JSSDK配置，所有页面共享', { cacheKey });
        } else {
          // 没有缓存或已过期，调用后端API获取JSSDK配置参数
          wechatConfig._log('获取当前页面URL', { url });
          response = await api.getWechatConfig({ url });
          wechatConfig._log('获取JSSDK配置参数', response.data);
          
          // 验证返回数据是否合法
          if (!response.data || !response.data.data || !response.data.data.config) {
            wechatConfig._log('微信JSSDK配置参数无效', response.data);
            return;
          }
          
          // 获取配置参数
          jssdkConfig = response.data.data.config;
          
          // 缓存配置，带时间戳
          wechatConfig._configCache[cacheKey] = {
            config: jssdkConfig,
            timestamp: now
          };
          wechatConfig._log('缓存JSSDK配置，所有页面共享', { cacheKey, expireTime: wechatConfig._cacheExpireTime });
        }
        
        // 保存当前初始化的URL
        wechatConfig._lastInitUrl = url;
        
        // 从正确的位置获取配置参数
        const { appId, timestamp, nonceStr, signature } = jssdkConfig;
        wechatConfig._log('解析JSSDK配置参数', { appId, timestamp, nonceStr, signature: signature.substring(0, 10) + '...' });
        
        // 检查必要参数是否存在
        if (!appId || !timestamp || !nonceStr || !signature) {
          wechatConfig._log('微信JSSDK配置参数不完整', { appId, timestamp, nonceStr, signature });
          return;
        }
        
        // 配置JSSDK
        wechatConfig._log('开始调用wx.config', { 
          debug: false, 
          appId, 
          jsApiList: ['updateAppMessageShareData', 'updateTimelineShareData', 'onMenuShareAppMessage', 'onMenuShareTimeline', 'onMenuShareQQ', 'onMenuShareWeibo'] 
        });
        
        wx.config({
          debug: false, // 生产环境建议关闭debug
          appId,
          timestamp,
          nonceStr,
          signature,
          jsApiList: [
            'updateAppMessageShareData', // 分享给朋友
            'updateTimelineShareData',   // 分享到朋友圈
            'onMenuShareAppMessage',     // 兼容旧版本分享给朋友
            'onMenuShareTimeline',       // 兼容旧版本分享到朋友圈
            'onMenuShareQQ',             // 分享到QQ
            'onMenuShareWeibo'           // 分享到微博
          ]
        });
        
        // 监听配置成功
        wx.ready(() => {
          wechatConfig._log('wx.ready 回调触发');
          
          // 标记为已初始化
          wechatConfig._isInitialized = true;
          
          // 设置分享内容
          wechatConfig.setupShare(shareConfig);
        });
        
        // 监听配置失败
        wx.error((res) => {
          wechatConfig._log('wx.error 回调触发', res);
          
          // 重置初始化状态
          wechatConfig._isInitialized = false;
          wechatConfig._lastInitUrl = null;
          
          // 处理常见错误
          if (res.errMsg.includes('invalid url domain')) {
            wechatConfig._log('微信JSSDK错误: 当前域名未在微信公众号后台配置，请在公众号管理后台添加可信域名');
          } else if (res.errMsg.includes('invalid signature')) {
            wechatConfig._log('微信JSSDK错误: 签名无效，请检查签名生成算法和参数');
          }
          // 微信JSSDK配置失败不影响页面正常显示
        });
        
      } catch (error) {
        wechatConfig._log('微信JSSDK初始化失败', error);
        // 微信JSSDK初始化失败不影响页面正常显示，仅记录日志
        // 重置初始化状态
        wechatConfig._isInitialized = false;
        wechatConfig._lastInitUrl = null;
      } finally {
        // 清除定时器
        wechatConfig._initTimer = null;
      }
    }, 300);
  },
  
  // 设置分享内容
  setupShare: (shareConfig = {}) => {
    // 只有在微信环境中才设置分享内容
    if (!wechatConfig.isWechatEnvironment()) {
      wechatConfig._log('当前非微信环境，跳过分享配置');
      return;
    }
    
    wechatConfig._log('开始设置分享内容', shareConfig);
    
    // 优化分享内容获取逻辑：优先使用传入的eventName，其次使用本地存储，最后使用默认值
    const eventName = shareConfig.eventName || localStorage.getItem('eventName') || '';
    const EventUID = localStorage.getItem('EventUID');
    
    wechatConfig._log('获取分享配置数据', { eventName, EventUID });
    
    // 生成包含EventUID的分享链接
    let link = window.location.href.split('#')[0];
    // 确保链接不包含Openid参数
    const urlObj = new URL(link);
    urlObj.searchParams.delete('Openid');
    
    // 只有在EventUID存在且不为空时才添加到链接
    if (EventUID && EventUID !== 'null' && EventUID !== 'undefined') {
      urlObj.searchParams.set('EventUID', EventUID);
    } else {
      // 如果EventUID无效，从 URL 中删除
      urlObj.searchParams.delete('EventUID');
    }
    
    link = urlObj.toString();
    wechatConfig._log('生成分享链接', { link });
    
    // 自定义分享配置
    const customConfig = {
      title: shareConfig.title || (eventName ? `${eventName} - 智武擎赛事系统` : '智武擎赛事系统'),
      desc: shareConfig.desc || '一站式赛事服务平台，可查询赛事信息、规程、日程安排、参赛队伍及成绩分数，欢迎使用！',
      link: shareConfig.link || link,
      imgUrl: shareConfig.imgUrl || 'https://wjdw-yun.ccswgfg.com/Comp/32.png', // 使用有效的分享LOGO
      success: shareConfig.success || (() => {
        wechatConfig._log('分享成功');
      }),
      cancel: shareConfig.cancel || (() => {
        wechatConfig._log('取消分享');
      })
    };
    
    wechatConfig._log('最终分享配置', { 
      title: customConfig.title,
      desc: customConfig.desc,
      link: customConfig.link,
      imgUrl: customConfig.imgUrl
    });
    
    // 确保微信JSSDK已经准备好
    if (typeof wx === 'undefined') {
      wechatConfig._log('wx对象未定义，跳过分享配置');
      return;
    }
    
    // 检查微信JSSDK是否已经准备好
    if (wx && typeof wx.updateAppMessageShareData === 'function') {
      // 分享给朋友（新版本）
      wechatConfig._log('调用wx.updateAppMessageShareData');
      wx.updateAppMessageShareData({
        title: customConfig.title,
        desc: customConfig.desc,
        link: customConfig.link,
        imgUrl: customConfig.imgUrl,
        success: customConfig.success,
        cancel: customConfig.cancel
      });
      
      // 分享到朋友圈（新版本）
      wechatConfig._log('调用wx.updateTimelineShareData');
      wx.updateTimelineShareData({
        title: customConfig.title,
        link: customConfig.link,
        imgUrl: customConfig.imgUrl,
        success: customConfig.success,
        cancel: customConfig.cancel
      });
    }
    
    // 兼容旧版本微信
    if (wx && typeof wx.onMenuShareAppMessage === 'function') {
      // 兼容旧版本分享给朋友
      wechatConfig._log('调用wx.onMenuShareAppMessage');
      wx.onMenuShareAppMessage({
        title: customConfig.title,
        desc: customConfig.desc,
        link: customConfig.link,
        imgUrl: customConfig.imgUrl,
        type: shareConfig.type || '', // 分享类型,music、video或link，不填默认为空
        dataUrl: shareConfig.dataUrl || '', // 如果type是music或video，则要提供数据链接，默认为空
        success: customConfig.success,
        cancel: customConfig.cancel
      });
      
      // 兼容旧版本分享到朋友圈
      wechatConfig._log('调用wx.onMenuShareTimeline');
      wx.onMenuShareTimeline({
        title: customConfig.title,
        link: customConfig.link,
        imgUrl: customConfig.imgUrl,
        success: customConfig.success,
        cancel: customConfig.cancel
      });
      
      // 分享到QQ
      wechatConfig._log('调用wx.onMenuShareQQ');
      wx.onMenuShareQQ({
        title: customConfig.title,
        desc: customConfig.desc,
        link: customConfig.link,
        imgUrl: customConfig.imgUrl,
        success: customConfig.success,
        cancel: customConfig.cancel
      });
      
      // 分享到微博
      wechatConfig._log('调用wx.onMenuShareWeibo');
      wx.onMenuShareWeibo({
        title: customConfig.title,
        desc: customConfig.desc,
        link: customConfig.link,
        imgUrl: customConfig.imgUrl,
        success: customConfig.success,
        cancel: customConfig.cancel
      });
    }
  },
  
  // 动态更新分享内容
  updateShare: (shareConfig = {}) => {
    // 只有在微信环境中才更新分享内容
    if (!wechatConfig.isWechatEnvironment()) {
      wechatConfig._log('当前非微信环境，跳过分享内容更新');
      return;
    }
    
    wechatConfig._log('动态更新分享内容', shareConfig);
    wechatConfig.setupShare(shareConfig);
  }
};

export default wechatConfig;